/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
/* eslint-disable react/no-this-in-sfc */
/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/no-floating-promises */

import { Fragment, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Group, Image, Rect } from 'react-konva';
import { useImage } from 'react-konva-utils';
import { useMutation, useQueryClient } from 'react-query';
import { KonvaEventObject } from 'konva/lib/Node';
import { Vector2d } from 'konva/lib/types';
import { useTheme } from 'styled-components';

import TickIcon from '@/assets/icons/tick.svg';
import {
  createDependency,
  deleteDependency,
  getAllRoadmapNodes,
  getInitiativeById,
  updateRoadmapNodeById,
} from '@/features/canvas/api';
import {
  type IsNodeActiveMap,
  type IsNodeDisabledMap,
  NodeDependencies,
} from '@/features/canvas/components/NodeDependencies/NodeDependencies';
import { NodeShape } from '@/features/canvas/components/NodeShape';
import {
  DEFAULT_NODE_RADIUS,
  HORIZONTAL_OFFSET,
  SIDEBAR_ACCORDION_KEY_MAP,
  VERTICAL_OFFSET,
} from '@/features/canvas/constants';
import { FilteredNodes, useEditorContext } from '@/features/canvas/contexts/editor-context';
import { HistoryActionType, useUndoRedo } from '@/features/canvas/contexts/undo-redo-context';
import { useInitiativeBudgets } from '@/features/canvas/hooks/useInitiativeBudgets';
import { calculateAbsolutePositionOnCanvasFromPercentageToPx } from '@/features/canvas/utils/calculate-absolute-position-on-canvas-in-px';
import { getIntersectionSectionAndTimePeriodId } from '@/features/canvas/utils/get-intersection-section-and-time-period-id';
import { getNodeRadius } from '@/features/canvas/utils/get-node-radius';
import { useShowToast } from '@/hooks/useShowToast';

const collectImmediateDependencies = (
  nodeId: number,
  nodes: FilteredNodes[]
): {
  connections: Set<string>;
  nodeIds: Set<number>;
} => {
  const nodeMap = new Map(nodes.map(n => [n.id, n]));
  const nodeIds = new Set<number>([nodeId]);
  const connections = new Set<string>();

  const currentNode = nodeMap.get(nodeId);
  if (!currentNode) return { nodeIds, connections };

  // Upstream dependencies
  (currentNode.dependencies || []).forEach(dep => {
    nodeIds.add(dep.masterNodeId);
    connections.add(`${dep.masterNodeId}-${dep.slaveNodeId}`);
  });

  // Downstream dependencies
  nodes.forEach(n => {
    (n.dependencies || []).forEach(dep => {
      if (dep.masterNodeId === nodeId) {
        nodeIds.add(dep.slaveNodeId);
        connections.add(`${dep.masterNodeId}-${dep.slaveNodeId}`);
      }
    });
  });

  return { nodeIds, connections };
};

const CanvasNodes = ({
  canvasWidthToFit,
  canvasHeightToFit,
}: {
  canvasHeightToFit: number;
  canvasWidthToFit: number;
}) => {
  const {
    roadmap,
    setSelectedNodeId,
    isPreview,
    filteredNodes,
    activeFilterIds,
    filters,
    optionCheckedIds,
    selectedNodeId,
    setExpandedSidebarAccordionKey,
    setIsInitiativeDetailDialogOpen,
    isPublicView,
    setFilteredNodes,
    canvasAreaRef,
    stageRef,
    canvasAreaOffset,
    showDependencies,
    isSelectingDependencies,
    setIsSelectingDependencies,
    filteredNodesRef,
    updateLastSavedTime,
    activeDependencyNodeIds,
    setActiveDependencyNodeIds,
    setActiveDependencyConnectionIds,
    activeDisabledConnectionIds,
    setActiveDisabledConnectionIds,
    viewMode,
    budgetViewTab,
    initiativeBudgets,
    isLoadingBudgets,
    toggledBudgetNodeIds,
    toggleBudgetNode,
  } = useEditorContext();
  useInitiativeBudgets();

  const [dependencyNodeRefs, setDependencyNodeRefs] = useState<Map<number, number>>(new Map());

  const [dependencyConnectionRefs, setDependencyConnectionRefs] = useState<Map<string, number>>(
    new Map()
  );

  const [activeDependencySources, setActiveDependencySources] = useState<
    Map<number, { connections: Set<string>; nodes: Set<number> }>
  >(new Map());

  const [hoveredNodeId, setHoveredNodeId] = useState<number | null>(null);

  useEffect(() => {
    if (!showDependencies) {
      setDependencyNodeRefs(new Map());
      setDependencyConnectionRefs(new Map());
      setActiveDependencySources(new Map());
    }
  }, [showDependencies]);

  const { addHistoryItem, updateResourceId } = useUndoRedo();
  const [tick] = useImage(TickIcon);

  const queryClient = useQueryClient();
  const theme = useTheme();
  const { t } = useTranslation();
  const { showToast } = useShowToast();

  const isView = isPreview || isPublicView;

  const { mutateAsync: createNodeDependency } = useMutation(createDependency, {
    onSuccess: ({ masterNodeId, slaveNodeId }) => {
      const newFilteredNodes = filteredNodesRef.current.map(n => {
        if (n.id === slaveNodeId) {
          return {
            ...n,
            dependencies: [
              ...(n.dependencies || []),
              {
                masterNodeId,
                slaveNodeId,
              },
            ],
          };
        }
        return n;
      });

      updateLastSavedTime();
      setFilteredNodes(newFilteredNodes as FilteredNodes[]);
    },
    onError: () => {
      showToast('error', t('editor.canvas.dependency_creation_error'));
    },
  });

  const { mutateAsync: deleteNodeDependency } = useMutation(deleteDependency, {
    onSuccess: (_, { slaveNodeId, masterNodeId }) => {
      const newFilteredNodes = filteredNodesRef.current.map(n => {
        if (n.id === slaveNodeId) {
          return {
            ...n,
            dependencies: n.dependencies?.filter(
              d => d.masterNodeId !== masterNodeId || d.slaveNodeId !== slaveNodeId
            ),
          };
        }
        return n;
      });
      setFilteredNodes(newFilteredNodes as FilteredNodes[]);
    },
    onError: () => {
      showToast('error', t('editor.canvas.dependency_deletion_error'));
    },
  });

  const { mutateAsync: updateNodeById } = useMutation(updateRoadmapNodeById, {
    onMutate: ({ nodeId, nodeBody }) => {
      queryClient.setQueryData(
        [getAllRoadmapNodes.name, Number(roadmap.id)],
        (oldNodes?: FilteredNodes[]) => {
          if (!oldNodes) return [];
          updateLastSavedTime();

          // Update the full dataset
          const updatedNodes = oldNodes.map(n => {
            if (n.id === nodeId) {
              return {
                ...n,
                ...nodeBody,
              };
            }
            return n;
          });

          // Also update filteredNodes for canvas display
          const updatedFilteredNodes = filteredNodes.map(n => {
            if (n.id === nodeId) {
              return {
                ...n,
                ...nodeBody,
              };
            }
            return n;
          });
          setFilteredNodes(updatedFilteredNodes);

          return updatedNodes;
        }
      );
    },
    onSuccess: () => updateLastSavedTime(),
  });

  const updateDependencyHighlighting = useCallback(
    (nodeId: number) => {
      const { nodeIds, connections } = collectImmediateDependencies(nodeId, filteredNodes);

      const updatedSources = new Map(activeDependencySources);
      const newNodeRefs = new Map(dependencyNodeRefs);
      const newConnectionRefs = new Map(dependencyConnectionRefs);
      const newDisabledConnections = new Set(activeDisabledConnectionIds);

      const isTogglingOff = updatedSources.has(nodeId);

      if (isTogglingOff) {
        const sourceData = updatedSources.get(nodeId)!;

        sourceData.nodes.forEach(id => {
          const prev = newNodeRefs.get(id) ?? 0;
          const next = prev - 1;
          if (next <= 0) newNodeRefs.delete(id);
          else newNodeRefs.set(id, next);
        });

        sourceData.connections.forEach(key => {
          const prev = newConnectionRefs.get(key) ?? 0;
          const next = prev - 1;
          if (next <= 0) {
            newConnectionRefs.delete(key);
            newDisabledConnections.delete(key);
          } else {
            newConnectionRefs.set(key, next);
          }
        });

        updatedSources.delete(nodeId);
      } else {
        nodeIds.forEach(id => {
          newNodeRefs.set(id, (newNodeRefs.get(id) || 0) + 1);
        });

        connections.forEach(key => {
          if (newDisabledConnections.has(key)) {
            newDisabledConnections.delete(key);
          }
          newConnectionRefs.set(key, (newConnectionRefs.get(key) || 0) + 1);
        });

        updatedSources.set(nodeId, {
          nodes: new Set(nodeIds),
          connections: new Set(connections),
        });
      }

      const keysToDelete: string[] = [];

      newConnectionRefs.forEach((_, key) => {
        const [masterId, slaveId] = key.split('-').map(Number);

        if (!newNodeRefs.has(masterId) || !newNodeRefs.has(slaveId)) {
          keysToDelete.push(key);
        }
      });

      keysToDelete.forEach(key => {
        newConnectionRefs.delete(key);
        newDisabledConnections.delete(key);
      });

      setDependencyNodeRefs(newNodeRefs);
      setDependencyConnectionRefs(newConnectionRefs);
      setActiveDependencyNodeIds(new Set(newNodeRefs.keys()));
      setActiveDependencyConnectionIds(new Set(newConnectionRefs.keys()));
      setActiveDisabledConnectionIds(newDisabledConnections);
      setActiveDependencySources(updatedSources);
    },
    [
      filteredNodes,
      dependencyNodeRefs,
      dependencyConnectionRefs,
      activeDependencySources,
      activeDisabledConnectionIds,
      setDependencyNodeRefs,
      setDependencyConnectionRefs,
      setActiveDependencyNodeIds,
      setActiveDependencyConnectionIds,
      setActiveDisabledConnectionIds,
      setActiveDependencySources,
    ]
  );

  const openNodeDialog = useCallback(
    ({ id }: { id: number }) => {
      setSelectedNodeId(id);
      setIsInitiativeDetailDialogOpen(true);
      if (isPublicView) {
        queryClient.setQueryData([getInitiativeById.name, id], () => {
          const initiative = filteredNodes.find(n => n.id === id)?.initiatives;
          return initiative;
        });
      }
    },
    [filteredNodes, isPublicView, queryClient, setIsInitiativeDetailDialogOpen, setSelectedNodeId]
  );

  const allNodeIdsInDependencyRelations = filteredNodes.reduce((acc, node) => {
    const nodeIds = [...acc];
    const slaveNodeIds = node.dependencies?.map(dep => dep.slaveNodeId) || [];
    const masterNodeIds = node.dependencies?.map(dep => dep.masterNodeId) || [];

    return [...nodeIds, ...slaveNodeIds, ...masterNodeIds];
  }, [] as number[]);

  const onClick = useCallback(
    (e: KonvaEventObject<MouseEvent>) => {
      const id = Number(e.target.parent?.id());
      const node = filteredNodes.find(n => n.id === id);
      if (!node) return;

      // In budget mode, handle node toggle
      if (viewMode === 'budget') {
        // If dependencies are shown and active, only allow interaction with nodes in chain
        if (showDependencies && activeDependencyNodeIds.size > 0) {
          if (!activeDependencyNodeIds.has(id)) return;
        }

        // If clicking on text, handle differently from shape click
        if (e.target.getClassName() === 'Text') {
          // In both view and edit modes, clicking text should open the sidebar/dialog
          setSelectedNodeId(node.id === selectedNodeId ? null : node.id!);
          setExpandedSidebarAccordionKey(SIDEBAR_ACCORDION_KEY_MAP.initiatives);
          setIsSelectingDependencies(false);

          if (isView) {
            openNodeDialog({ id });
          }
          return;
        }

        // For shape clicks only, toggle the node in budget mode
        toggleBudgetNode(id);
        return;
      }

      // Filter mode logic below (unchanged)
      if (showDependencies && e.target.getClassName() === 'Text') {
        if (isView) {
          openNodeDialog({ id });
        }
        return;
      }

      if (showDependencies) {
        const isIsolated =
          !node.dependencies?.length && !allNodeIdsInDependencyRelations.includes(id);

        if (isIsolated) return;

        updateDependencyHighlighting(id);
        return;
      }

      if (e.target.getClassName() !== 'Text') return;

      setSelectedNodeId(node.id === selectedNodeId ? null : node.id!);
      setExpandedSidebarAccordionKey(SIDEBAR_ACCORDION_KEY_MAP.initiatives);
      setIsSelectingDependencies(false);

      if (isView) {
        openNodeDialog({ id: node.id! });
      }
    },
    [
      allNodeIdsInDependencyRelations,
      filteredNodes,
      isView,
      openNodeDialog,
      selectedNodeId,
      setExpandedSidebarAccordionKey,
      setIsSelectingDependencies,
      setSelectedNodeId,
      showDependencies,
      updateDependencyHighlighting,
      viewMode,
      activeDependencyNodeIds,
      toggleBudgetNode,
    ]
  );

  const onDependencyClick = useCallback(
    async ({
      evt,
      slaveNodeId,
      dependencyExist,
    }: {
      dependencyExist: boolean;
      evt: KonvaEventObject<MouseEvent>;
      slaveNodeId?: number;
    }) => {
      if (!selectedNodeId) return;

      const masterNodeId = Number(evt.target.parent?.id());

      if (!dependencyExist) {
        const newlyCreatedDependency = await createNodeDependency({
          masterNodeId,
          slaveNodeId: selectedNodeId,
        });

        addHistoryItem({
          type: HistoryActionType.AddDependency,
          undo: async (resourceIds?: number[]) => {
            await deleteNodeDependency({
              masterNodeId: resourceIds?.[1] || newlyCreatedDependency.masterNodeId!,
              slaveNodeId: resourceIds?.[0]! || newlyCreatedDependency.slaveNodeId,
            });
          },
          async redo(redoResourceIds?: number[]) {
            const redoCreatedDependency = await createNodeDependency({
              masterNodeId: redoResourceIds?.[1] || masterNodeId,
              slaveNodeId: redoResourceIds?.[0] || slaveNodeId || selectedNodeId,
            });
            updateResourceId(
              this.type,
              redoResourceIds?.[1] || newlyCreatedDependency.masterNodeId!,
              redoCreatedDependency.slaveNodeId!
            );
            this.undo = async (resourceIds?: number[]) => {
              await deleteNodeDependency({
                masterNodeId: resourceIds?.[1] || redoCreatedDependency.masterNodeId!,
                slaveNodeId: resourceIds?.[0]!,
              });
            };
            this.resourceIds = [
              redoResourceIds?.[0] || slaveNodeId || selectedNodeId,
              redoResourceIds?.[1] || newlyCreatedDependency.masterNodeId!,
            ];
          },
          resourceIds: [slaveNodeId || selectedNodeId, newlyCreatedDependency.masterNodeId!],
        });

        return;
      }
      await deleteNodeDependency({ masterNodeId, slaveNodeId: slaveNodeId || selectedNodeId });
      addHistoryItem({
        type: HistoryActionType.DeleteDependency,
        async undo(resourceIds?: number[]) {
          const createdDependency = await createNodeDependency({
            masterNodeId: resourceIds?.[1] || masterNodeId,
            slaveNodeId: resourceIds?.[0] || slaveNodeId || selectedNodeId,
          });
          this.redo = async (redoResourceIds?: number[]) => {
            await deleteNodeDependency({
              masterNodeId: redoResourceIds?.[1] || createdDependency.masterNodeId!,
              slaveNodeId: redoResourceIds?.[0] || createdDependency.slaveNodeId,
            });
          };
          this.resourceIds = [
            resourceIds?.[0] || slaveNodeId || selectedNodeId,
            resourceIds?.[1] || createdDependency.masterNodeId!,
          ];
        },
        redo: async (resourceIds?: number[]) => {
          await deleteNodeDependency({
            masterNodeId: resourceIds?.[1] || masterNodeId,
            slaveNodeId: resourceIds?.[0] || slaveNodeId || selectedNodeId,
          });
        },
        resourceIds: [slaveNodeId || selectedNodeId, masterNodeId],
      });
    },
    [selectedNodeId, deleteNodeDependency, addHistoryItem, createNodeDependency, updateResourceId]
  );

  const updateNodeValue = useCallback(
    ({
      sectionId,
      timePeriodId,
      nodeId,
      position,
    }: {
      nodeId: number;
      position: Vector2d;
      sectionId: number;
      timePeriodId: number;
    }) => {
      const newlyUpdatedNodes = filteredNodes.map(node => {
        if (node.id === nodeId) {
          return {
            ...node,
            x: position.x,
            y: position.y,
            sectionId,
            timePeriodId,
          };
        }
        return node;
      });

      setFilteredNodes(newlyUpdatedNodes as FilteredNodes[]);
    },
    [filteredNodes, setFilteredNodes]
  );

  const updateNode = useCallback(
    async (e: KonvaEventObject<DragEvent>) => {
      const canvasDropArea = canvasAreaRef?.current;
      const id = Number(e.target.id());
      const targetPosition = e.target.position();
      const foundNode = filteredNodes.find(n => n.id === id)!;

      const { color, shape, size, ...node } = foundNode;

      const intersections = stageRef.current?.getAllIntersections(targetPosition);

      if (
        !targetPosition ||
        !canvasDropArea ||
        !intersections?.length ||
        intersections.length < 2
      ) {
        return showToast('error', t('editor.canvas.initiative_position_error'));
      }

      const offset = canvasDropArea.getAbsolutePosition();

      const { timePeriodId, sectionId } = getIntersectionSectionAndTimePeriodId(intersections);

      if (!timePeriodId || !sectionId) {
        return showToast('error', t('editor.canvas.initiative_position_error'));
      }

      const newInitiativeCanvasXPercentage = Number(
        (
          ((targetPosition.x - offset.x - DEFAULT_NODE_RADIUS) / canvasDropArea.width()) *
          100
        ).toFixed(2)
      );

      const newInitiativeCanvasYPercentage = Number(
        (
          ((targetPosition.y - offset.y - DEFAULT_NODE_RADIUS) / canvasDropArea.height()) *
          100
        ).toFixed(2)
      );

      updateNodeValue({
        nodeId: node.id!,
        sectionId: Number(sectionId),
        timePeriodId: Number(timePeriodId),
        position: { x: newInitiativeCanvasXPercentage, y: newInitiativeCanvasYPercentage },
      });

      const undoNode = JSON.parse(JSON.stringify(node)) as typeof node;

      addHistoryItem({
        type: HistoryActionType.UpdateNode,
        redo: (resourceIds?: number[]) => {
          updateNodeValue({
            nodeId: resourceIds?.[0] || undoNode.id!,
            sectionId: Number(sectionId),
            timePeriodId: Number(timePeriodId),
            position: { x: newInitiativeCanvasXPercentage, y: newInitiativeCanvasYPercentage },
          });

          updateNodeById({
            nodeBody: {
              ...node,
              x: newInitiativeCanvasXPercentage,
              y: newInitiativeCanvasYPercentage,
              sectionId: Number(sectionId),
              timePeriodId: Number(timePeriodId),
              id: resourceIds?.[0] || node.id!,
              initiatives: node.initiatives
                ? {
                    ...node.initiatives,
                    id: resourceIds?.[1],
                  }
                : {
                    title: node.title,
                  },
            },
            nodeId: resourceIds?.[0] || node.id!,
          });
        },
        undo: (resourceIds?: number[]) => {
          updateNodeValue({
            nodeId: resourceIds?.[0] || undoNode.id!,
            sectionId: Number(sectionId),
            timePeriodId: Number(timePeriodId),
            position: { x: undoNode.x, y: undoNode.y },
          });

          updateNodeById({
            nodeBody: {
              ...undoNode,
              sectionId: Number(sectionId),
              timePeriodId: Number(timePeriodId),
              id: resourceIds?.[0] || node.id!,
              initiatives: undoNode.initiatives
                ? {
                    ...undoNode.initiatives,
                    id: resourceIds?.[1] || undoNode.initiatives.id!,
                  }
                : {
                    title: undoNode.title,
                  },
            },
            nodeId: resourceIds?.[0] || node.id!,
          });
        },
        resourceIds: [node.id!, node.initiatives?.id].filter(Boolean) as number[],
      });

      await updateNodeById({
        nodeBody: {
          ...node,
          x: newInitiativeCanvasXPercentage,
          y: newInitiativeCanvasYPercentage,
          sectionId: Number(sectionId),
          timePeriodId: Number(timePeriodId),
          id: node.id!,
        },
        nodeId: node.id!,
      });

      await queryClient.invalidateQueries(['roadmap-budget-summary', roadmap.id]);
      await queryClient.invalidateQueries(['budget-by-time-period', roadmap.id]);
      await queryClient.invalidateQueries(['budget-by-section', roadmap.id]);
    },
    [
      addHistoryItem,
      canvasAreaRef,
      filteredNodes,
      showToast,
      stageRef,
      t,
      updateNodeById,
      updateNodeValue,
      queryClient,
      roadmap.id,
    ]
  );

  const checkBoundaries = (e: KonvaEventObject<DragEvent>) => {
    const canvasDropArea = canvasAreaRef?.current;
    const position = canvasDropArea?.getRelativePointerPosition();
    if (!canvasDropArea || !position) return;

    const { x, y } = position;

    let newX = x;
    let newY = y;

    // for some reason intersections are not found when x or y is max value (width or height)
    //  - 1 is to avoid this for now
    if (newX - DEFAULT_NODE_RADIUS < 0) {
      newX = 0 + DEFAULT_NODE_RADIUS;
    }
    if (newX + DEFAULT_NODE_RADIUS > canvasDropArea.width()) {
      newX = canvasDropArea.width() - DEFAULT_NODE_RADIUS - 1;
    }

    if (newY - DEFAULT_NODE_RADIUS < 0) {
      newY = 0 + DEFAULT_NODE_RADIUS;
    }
    if (newY + DEFAULT_NODE_RADIUS > canvasDropArea.height()) {
      newY = canvasDropArea.height() - DEFAULT_NODE_RADIUS - 1;
    }

    const layerOffset = canvasDropArea.getAbsolutePosition();
    e.target.setAbsolutePosition({ x: newX + layerOffset.x, y: newY + layerOffset.y });
  };

  const selectedNode = filteredNodes.find(n => n.id === selectedNodeId);

  const sortedNodesByRenderOrder = [...filteredNodes].sort(
    (a, b) => (b.size ?? DEFAULT_NODE_RADIUS) - (a.size ?? DEFAULT_NODE_RADIUS)
  );

  const isNodeActive: IsNodeActiveMap = new Map();
  const isNodeDisabled: IsNodeDisabledMap = new Map();

  return (
    <Group key="nodes" offset={{ x: VERTICAL_OFFSET, y: HORIZONTAL_OFFSET }}>
      {sortedNodesByRenderOrder.map(node => {
        const activeFilters =
          filters?.filter(f => f.id !== undefined && activeFilterIds.includes(f.id!)) || [];

        const doesNodeHaveActiveFilter = activeFilters.some(filter => {
          return filter.nodes?.some(n => n.nodeId === node.id) ?? false;
        });

        const isNodesOptionChecked = activeFilters.every(filter =>
          filter.selection === 'multiple'
            ? filter.nodes?.some(
                n => n.nodeId === node.id && optionCheckedIds.includes(n.optionId ?? -1)
              ) ?? false
            : optionCheckedIds.includes(
                filter.nodes?.find(n => n.nodeId === node.id)?.optionId ?? -1
              )
        );

        const hasNoDependencyAndIsNotDependency =
          !allNodeIdsInDependencyRelations.includes(node.id!) && !node.dependencies?.length;

        const isNotInFilter = Boolean(
          (doesNodeHaveActiveFilter && optionCheckedIds.length > 0 && !isNodesOptionChecked) ||
            (!doesNodeHaveActiveFilter && activeFilterIds.length > 0)
        );

        const { size } = node;
        let color = node.color || theme.palette.brand.editorTabPrimary;
        let opacity = 1;

        // Calculate if node is greyed out
        const isGreyDueToFilter = activeFilterIds.length > 0 && isNotInFilter;
        const isGreyDueToOrphan = showDependencies && hasNoDependencyAndIsNotDependency;
        const isGreyDueToOtherChain =
          showDependencies &&
          activeDependencyNodeIds.size > 0 &&
          !activeDependencyNodeIds.has(node.id!);

        const isGreyedOut = isGreyDueToFilter || isGreyDueToOrphan || isGreyDueToOtherChain;

        const isDisabled = isGreyDueToFilter || isGreyDueToOrphan;

        isNodeDisabled.set(node.id!, isDisabled);

        isNodeActive.set(node.id!, !isGreyedOut);

        // In budget mode with dependencies, grey out nodes that are toggled off
        const isBudgetModeWithDependencies =
          viewMode === 'budget' && activeDependencyNodeIds.size > 0;
        const isToggledOffInBudget = toggledBudgetNodeIds.has(node.id!);

        if (isGreyedOut || (isBudgetModeWithDependencies && isToggledOffInBudget)) {
          color = '#6e6e72';
          opacity = 0.15;
        }

        const { x, y } = calculateAbsolutePositionOnCanvasFromPercentageToPx({
          height: canvasHeightToFit,
          width: canvasWidthToFit,
          xPercentage: node.x,
          yPercentage: node.y,
          offsetX: canvasAreaOffset.x,
          offsetY: canvasAreaOffset.y,
          additionX: DEFAULT_NODE_RADIUS,
          additionY: DEFAULT_NODE_RADIUS,
        });

        const isMasterOfSelectedNodeDependency = selectedNode?.dependencies?.some(
          d => d.masterNodeId === node.id
        );

        const isToggledOff = viewMode === 'budget' && toggledBudgetNodeIds.has(node.id!);

        // In budget mode, check if node can be interacted with
        const canInteractInBudgetMode =
          viewMode === 'budget'
            ? activeDependencyNodeIds.size === 0 || activeDependencyNodeIds.has(node.id!)
            : true;

        return (
          <Fragment key={`container-node-group-${node.id}`}>
            {node.isVisible && (
              <Group key={`node-group-${node.id}`}>
                <NodeShape
                  onClick={canInteractInBudgetMode ? onClick : undefined}
                  onDblClick={
                    canInteractInBudgetMode ? () => openNodeDialog({ id: node.id! }) : undefined
                  }
                  active={Number(selectedNodeId) === Number(node.id) && !isPreview}
                  disabled={isDisabled || (viewMode === 'budget' && !canInteractInBudgetMode)}
                  onDragEnd={updateNode}
                  onDragMove={checkBoundaries}
                  id={node.id!}
                  key={node.id!.toString()}
                  title={
                    node.initiatives?.title || `${node.title || t('untitled')} - Missing Initiative`
                  }
                  x={x}
                  y={y}
                  fill={color}
                  shape={node.shape}
                  draggable={!isPreview && canInteractInBudgetMode}
                  opacity={opacity}
                  size={size}
                  showDependencies={showDependencies}
                  isHovered={hoveredNodeId === node.id && canInteractInBudgetMode}
                  onMouseEnter={
                    canInteractInBudgetMode ? () => setHoveredNodeId(node.id!) : undefined
                  }
                  onMouseLeave={canInteractInBudgetMode ? () => setHoveredNodeId(null) : undefined}
                  // Add these new props
                  viewMode={viewMode}
                  budgetViewTab={budgetViewTab}
                  budgetData={
                    node.initiatives?.id ? initiativeBudgets.get(node.initiatives.id) : undefined
                  }
                  isLoadingBudget={isLoadingBudgets}
                  isToggledOff={isToggledOff}
                  initiative={node.initiatives}
                />
                {(isSelectingDependencies || showDependencies) && (
                  <Group>
                    {selectedNodeId !== node.id && isSelectingDependencies && (
                      <Group
                        key={`${node.id}-checkbox`}
                        id={node.id!.toString()}
                        onMouseEnter={e => {
                          const stage = e.target.getStage();
                          if (stage) {
                            const container = stage.container();
                            container.style.cursor = 'pointer';
                          }
                        }}
                        onMouseLeave={e => {
                          const stage = e.target.getStage();
                          if (stage) {
                            const container = stage.container();
                            container.style.cursor = 'default';
                          }
                        }}
                        onClick={async e => {
                          const masterNodeId = Number(e.target.parent?.id());
                          const dependencyExist = Boolean(
                            selectedNode?.dependencies?.some(
                              d =>
                                d.masterNodeId === masterNodeId && d.slaveNodeId === selectedNodeId
                            )
                          );
                          await onDependencyClick({
                            evt: e,
                            dependencyExist,
                            slaveNodeId: selectedNodeId!,
                          });
                        }}
                      >
                        <Rect
                          x={x - getNodeRadius(node.size) - 25}
                          y={y - DEFAULT_NODE_RADIUS}
                          width={DEFAULT_NODE_RADIUS * 2}
                          height={DEFAULT_NODE_RADIUS * 2}
                          strokeWidth={1}
                          stroke={theme.palette.brand.textPrimary}
                        />
                        {isMasterOfSelectedNodeDependency && (
                          <Image
                            image={tick}
                            x={x - getNodeRadius(node.size) - 22}
                            y={y - DEFAULT_NODE_RADIUS / 2 - 2}
                            width={DEFAULT_NODE_RADIUS * 2 - 5}
                            height={DEFAULT_NODE_RADIUS * 2 - 5}
                          />
                        )}
                      </Group>
                    )}
                    {((selectedNodeId === node.id && isSelectingDependencies) ||
                      (!isSelectingDependencies && showDependencies)) && (
                      <NodeDependencies
                        key={`${node.id}-dependencies`}
                        nodeDependencies={node.dependencies || []}
                        nodeId={node.id!}
                        nodePosition={{ x, y }}
                        canvasHeightToFit={canvasHeightToFit}
                        canvasWidthToFit={canvasWidthToFit}
                        offset={canvasAreaOffset}
                        isNodeActive={isNodeActive}
                        isNodeDisabled={isNodeDisabled}
                      />
                    )}
                  </Group>
                )}
              </Group>
            )}
          </Fragment>
        );
      })}
    </Group>
  );
};

export { CanvasNodes };
